import { observable, action, computed } from 'mobx';
import transitionPath, { shouldUpdateNode } from 'router5-transition-path';

export class RouterStore {
  @observable.ref route = null;
  @observable.ref previousRoute = null;
  @observable.ref transitionRoute = null;
  @observable.ref transitionError = null;
  @observable.ref intersectionNode = '';
  @observable.ref toActivate = [];
  @observable.ref toDeactivate = [];
  @observable params = observable.map();

  router = null;

  constructor() {
    this.navigate = this.navigate.bind(this);
    this.shouldUpdateNodeFactory = this.shouldUpdateNodeFactory.bind(this);
  }

  setRouter(router) {
    this.router = router;
  }

  updateRoute(routeType, route) {
    if (routeType === 'route' && route) {
      this.params.clear();
      this.params.merge(route.params);
    }
    this[routeType] = route;
  }

  resetRoute(routeType) {
    this[routeType] = null;
  }

  //  ============
  //  = COMPUTED =
  //  ============
  @computed get path() {
    return this.route.path;
  }

  //  ===========
  //  = ACTIONS =
  //  ===========
  // These are called by the plugin
  @action onTransitionStart = route => {
    this.updateRoute('transitionRoute', route);
    this.transitionError = null;
  };

  @action onTransitionSuccess = (route, previousRoute, opts) => {
    this.updateRoute('route', route);
    this.updateRoute('previousRoute', previousRoute);
    if (route) {
      const { intersection, toActivate, toDeactivate } = transitionPath(
        route,
        previousRoute,
      );
      this.intersectionNode = opts.reload ? '' : intersection;
      this.toActivate = toActivate;
      this.toDeactivate = toDeactivate;
    }
    this.clearErrors();
  };

  @action onTransitionCancel = () => {
    this.resetRoute('transitionRoute');
  };

  @action onTransitionError = (route, previousRoute, transitionError) => {
    this.updateRoute('transitionRoute', route);
    this.updateRoute('previousRoute', previousRoute);
    this.transitionError = transitionError;
  };

  // These can be called manually
  @action clearErrors = () => {
    this.resetRoute('transitionRoute');
    this.transitionError = null;
  };

  // Public API, we can manually call these router methods
  // Note: These are not actions because they don't directly modify the state

  // Just an alias
  @action navigate = (name, params, opts) => {
    if (this.router) {
      this.router.navigate(name, params, opts);
    }
  };

  // Utility to calculate which react routeNode should update
  shouldUpdateNodeFactory = nodeName => {
    return computed(() => {
      return shouldUpdateNode(nodeName)(this.route, this.previousRoute);
    });
  };
}

export function mobxPluginFactory(routerStore) {
  function mobxPlugin(router) {
    // NOTE: cross-referencing objects
    router.setDependency('routerStore', routerStore);
    routerStore.setRouter(router);

    // Implemented methods
    return {
      onTransitionStart(toState, fromState) {
        routerStore.onTransitionStart(toState, fromState);
      },
      onTransitionSuccess(toState, fromState, opts) {
        routerStore.onTransitionSuccess(toState, fromState, opts);
      },
      onTransitionCancel(toState, fromState) {
        routerStore.onTransitionCancel(toState, fromState);
      },
      onTransitionError(toState, fromState, err) {
        routerStore.onTransitionError(toState, fromState, err);
      },
    };
  }

  mobxPlugin.pluginName = 'MOBX_PLUGIN';

  return mobxPlugin;
}
